import React, { useState } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const SocialConnect = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const socialPlatforms = [
    {
      name: "Instagram",
      icon: "Instagram",
      handle: "@skycreststudios",
      followers: "25.4K",
      description: "Behind-the-scenes content and daily inspiration",
      color: "text-pink-600",
      bgColor: "bg-pink-50",
    },
    {
      name: "YouTube",
      icon: "Youtube",
      handle: "Skycrest Studios",
      followers: "12.8K",
      description: "Full project showcases and tutorials",
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
    {
      name: "LinkedIn",
      icon: "Linkedin",
      handle: "Skycrest Studios",
      followers: "8.2K",
      description: "Industry insights and business content",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      name: "TikTok",
      icon: "Music",
      handle: "@skycreststudios",
      followers: "18.7K",
      description: "Quick tips and creative process videos",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  const categoryContent = {
    all: {
      title: "Latest from All Platforms",
      posts: [
        {
          platform: "Instagram",
          image:
            "https://images.unsplash.com/photo-1519741497674-611481863552?w=300&h=300&fit=crop",
          caption:
            "Behind the scenes of Sarah & Mike's wedding at Central Park",
          likes: "1,247",
          comments: "89",
        },
        {
          platform: "YouTube",
          image:
            "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=300&fit=crop",
          caption: "How we shot this luxury penthouse tour in one day",
          likes: "892",
          comments: "156",
        },
        {
          platform: "LinkedIn",
          image:
            "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=300&h=300&fit=crop",
          caption: "The future of video marketing: 5 trends to watch",
          likes: "445",
          comments: "67",
        },
      ],
    },
    wedding: {
      title: "Wedding & Events Content",
      posts: [
        {
          platform: "Instagram",
          image:
            "https://images.unsplash.com/photo-1519741497674-611481863552?w=300&h=300&fit=crop",
          caption: "Romantic sunset ceremony at Riverside Gardens",
          likes: "2,156",
          comments: "234",
        },
        {
          platform: "Instagram",
          image:
            "https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?w=300&h=300&fit=crop",
          caption: "First dance magic captured in cinematic style",
          likes: "1,789",
          comments: "178",
        },
      ],
    },
    realestate: {
      title: "Real Estate Showcases",
      posts: [
        {
          platform: "YouTube",
          image:
            "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=300&fit=crop",
          caption: "Modern penthouse tour with drone footage",
          likes: "1,234",
          comments: "89",
        },
        {
          platform: "Instagram",
          image:
            "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=300&h=300&fit=crop",
          caption: "Architectural beauty meets video storytelling",
          likes: "987",
          comments: "67",
        },
      ],
    },
  };

  const categories = [
    { id: "all", name: "All Content", icon: "Grid" },
    { id: "wedding", name: "Weddings", icon: "Heart" },
    { id: "realestate", name: "Real Estate", icon: "Home" },
    { id: "marketing", name: "Marketing", icon: "Megaphone" },
  ];

  const currentContent =
    categoryContent?.[activeCategory] || categoryContent?.all;

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-fluid-3xl font-display font-bold text-primary mb-4">
              Connect & Follow Our Journey
            </h2>
            <p className="text-fluid-lg text-muted-foreground max-w-2xl mx-auto">
              Stay inspired with behind-the-scenes content, industry insights,
              and our latest creative work across all platforms.
            </p>
          </div>

          {/* Social Platforms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {socialPlatforms?.map((platform, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-lg p-6 text-center hover-lift transition-smooth"
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${platform?.bgColor}`}
                >
                  <Icon
                    name={platform?.icon}
                    size={28}
                    className={platform?.color}
                  />
                </div>

                <h3 className="text-lg font-semibold text-card-foreground mb-1">
                  {platform?.name}
                </h3>

                <p className="text-sm text-muted-foreground mb-2">
                  {platform?.handle}
                </p>

                <p className="text-lg font-bold text-accent mb-3">
                  {platform?.followers}
                </p>

                <p className="text-xs text-muted-foreground mb-4">
                  {platform?.description}
                </p>

                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Plus"
                  iconPosition="left"
                >
                  Follow
                </Button>
              </div>
            ))}
          </div>

          {/* Content Categories */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-smooth ${
                  activeCategory === category?.id
                    ? "bg-accent text-accent-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent/20"
                }`}
              >
                <Icon name={category?.icon} size={16} />
                <span>{category?.name}</span>
              </button>
            ))}
          </div>

          {/* Social Content Feed */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-primary mb-6 text-center">
              {currentContent?.title}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentContent?.posts?.map((post, index) => (
                <div
                  key={index}
                  className="bg-card border border-border rounded-lg overflow-hidden hover-lift transition-smooth"
                >
                  <div className="relative h-64 overflow-hidden">
                    <Image
                      src={post?.image}
                      alt={post?.caption}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-background/90 text-foreground px-2 py-1 rounded text-xs font-medium">
                        {post?.platform}
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <p className="text-sm text-card-foreground mb-3 line-clamp-2">
                      {post?.caption}
                    </p>

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <Icon name="Heart" size={12} />
                          <span>{post?.likes}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="MessageCircle" size={12} />
                          <span>{post?.comments}</span>
                        </span>
                      </div>
                      <button className="text-accent hover:text-accent/80 transition-fast">
                        View Post
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialConnect;
